<modal-dialog #newRoleModal [dialogTemplate]="newRoleDialogTemplate"></modal-dialog>
<ng-template #newRoleDialogTemplate>
  <div
    class="light-content-panel light-control-host"
    (keydown.escape)="close()"
    tabindex="0"
    arpSurfaceErrorHost="new-role-modal"
    #dialogError="surfaceErrorHost"
  >
    <div class="modal-header">
      <h2 class="panel-title margin-left">Create Custom Role</h2>
      <button type="button" class="modal-close-btn" (click)="close()">
        <svg lucideX></svg>
      </button>
    </div>
    <form
      [formGroup]="roleForm"
      (ngSubmit)="onCreateRole()"
      (keydown.enter)="$event.preventDefault()"
      [arpFieldErrorHost]="roleForm"
      [fieldErrorMap]="{ 'role-name': 'name' }"
      fieldErrorHostId="new-role-modal"
    >
      <div class="modal-body">
        <!-- Role Information Section -->
        <div class="form-section">
          <h3>Role Information</h3>
          <div class="role-information-body">
            <div class="flex-row gap-4">
              <div class="form-row flex-1">
                <div class="form-group">
                  <label class="form-label">Role Name</label>
                  <input
                    type="text"
                    formControlName="name"
                    class="arpio-input full-width"
                    placeholder="Your role name..."
                  />
                  <arp-field-issue [message]="nameErrors" [control]="roleForm.get('name')"></arp-field-issue>
                </div>
              </div>

              <div class="form-row flex-1">
                <div class="form-group">
                  <label class="form-label"
                    >External ID
                    <span class="input-help-text text-xs">(Optional ID used to match SSO role claims)</span></label
                  >
                  <input
                    type="text"
                    formControlName="externalId"
                    class="arpio-input full-width"
                    placeholder="external-role-id (letters, numbers, hyphens, underscores only)"
                  />
                  <arp-field-issue [message]="externalIdErrors"></arp-field-issue>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Policies Section -->
        <div class="form-section">
          <h3>Assign Policies</h3>
          <p class="heading-sub-p-text">
            Create policies to combine a selected access level with a group of applications.
          </p>

          <div formArrayName="policyChildren">
            @for (policyChild of policyChildrenArray.controls; track policyChild; let i = $index) {
              <div [formGroupName]="i" class="policy-child">
                <div class="policy-child-header">
                  <!-- Policy Name and Toggle -->
                  <div class="policy-header-content" (click)="togglePolicyCollapsed(i)">
                    <!-- Collapse Toggle -->
                    <div class="collapse-toggle">
                      @if (isPolicyCollapsed(i)) {
                        <svg lucideChevronRight></svg>
                      } @else {
                        <svg lucideChevronDown></svg>
                      }
                    </div>
                    <!-- Policy Name -->
                    <div class="policy-name-display">Policy {{ i + 1 }}</div>
                  </div>
                  <!-- Action Buttons -->
                  <div class="policy-actions">
                    <button
                      type="button"
                      class="icon-button danger"
                      (click)="removePolicyChild(i); $event.stopPropagation()"
                      [disabled]="policyChildrenArray.length <= 1"
                      title="Remove Policy"
                    >
                      <svg lucideTrash2></svg>
                    </button>
                  </div>
                </div>
                <div class="policy-child-body" [class.collapsed]="isPolicyCollapsed(i)">
                  <!-- Policy Validation Error -->
                  <arp-field-issue class="margin-top" [message]="getPolicyErrors(i)"></arp-field-issue>
                  <!-- Access Level Selection -->
                  <div class="form-group">
                    <label class="form-label">Access Level</label>
                    <div class="flex gap-4 items-end">
                      <div class="select-wrapper" style="flex: 1">
                        <select class="arpio-select full-width" formControlName="accessLevel">
                          <option value="">Select Access Level</option>
                          @for (level of accessLevels; track level) {
                            <option [value]="level.value">
                              {{ level.label }}
                            </option>
                          }
                        </select>
                      </div>
                    </div>
                  </div>
                  <!-- Application Selection - Only show for app-level permissions -->
                  <div class="application-selection">
                    <div class="flex items-center gap-4 margin-bottom">
                      <input
                        type="checkbox"
                        class="arpio-checkbox"
                        formControlName="allApplications"
                        (change)="onAllApplicationsChange(i, $event.target.checked)"
                      />
                      <label class="checkbox-label">Always Include All Applications</label>
                    </div>
                    <div>OR</div>
                    <div [class.disabled-section]="policyChild.get('allApplications')?.value" class="application-table">
                      <div class="select-applications-header flex-row items-center gap-4 margin-bottom">
                        <div>
                          <label>Select Individual Applications</label>
                        </div>
                        <div class="filter-container">
                          <input
                            pInputText
                            type="text"
                            #appFilter
                            (input)="appTable.filterGlobal(appFilter.value, 'contains')"
                            placeholder="Filter applications..."
                            class="arpio-input filter-input"
                          />
                        </div>
                      </div>
                      <p-table
                        #appTable
                        [value]="availableApplications"
                        [globalFilterFields]="['name', 'type']"
                        dataKey="id"
                        [scrollable]="true"
                        scrollHeight="200px"
                        [tableStyle]="{ 'min-width': '30rem' }"
                        class="p-datatable-sm"
                      >
                        <ng-template #header>
                          <tr>
                            <th class="checkbox-column">
                              <input
                                type="checkbox"
                                class="arpio-checkbox"
                                [checked]="areAllApplicationsSelected(i)"
                                [indeterminate]="areSomeApplicationsSelected(i)"
                                (change)="toggleAllApplications(i, $event)"
                              />
                            </th>
                            <th pSortableColumn="name">Application Name <p-sort-icon field="name"></p-sort-icon></th>
                            @if (azureEnabled) {
                              <th pSortableColumn="type">Cloud Provider <p-sort-icon field="type"></p-sort-icon></th>
                            }
                          </tr>
                        </ng-template>
                        <ng-template #body let-application>
                          <tr>
                            <td class="checkbox-column">
                              <input
                                type="checkbox"
                                class="arpio-checkbox"
                                [checked]="isApplicationSelected(i, application)"
                                (change)="toggleApplication(i, application, $event)"
                              />
                            </td>
                            <td>{{ application.name }}</td>
                            @if (azureEnabled) {
                              <td>
                                <span
                                  class="app-type-badge"
                                  [attr.data-type]="application.type.toLowerCase().replace(' ', '-')"
                                >
                                  {{ appTypeLabel(application.type) }}
                                </span>
                              </td>
                            }
                          </tr>
                        </ng-template>
                        <ng-template #emptymessage>
                          <tr>
                            <td colspan="3" class="empty-state-cell text-center">
                              @if (!loadingApplications) {
                                <div class="empty-state-content">
                                  <p class="empty-state-submessage">
                                    Add applications to your account to assign permissions.
                                  </p>
                                </div>
                              }
                              @if (loadingApplications) {
                                <div class="loading-state-content">
                                  <svg lucideLoaderCircle class="lucide-spin"></svg>
                                  <p class="loading-state-message">Loading applications...</p>
                                </div>
                              }
                            </td>
                          </tr>
                        </ng-template>
                        <ng-template #summary>
                          <div class="flex align-items-center justify-content-between">
                            <span
                              >Displaying {{ appTable.filteredValue?.length ?? appTable.value?.length }} of
                              {{ appTable.value?.length }} applications</span
                            >
                          </div>
                        </ng-template>
                      </p-table>
                    </div>
                  </div>
                </div>
              </div>
            }
            <!-- Add Another Policy Link -->
            <div class="margin-top">
              <a class="underline" (click)="addPolicyChild()">
                <svg lucidePlus class="text-sm"></svg> Add Another Policy
              </a>
            </div>
          </div>
        </div>
      </div>

      @if (dialogError.error) {
        <arp-error-block
          class="dialog-error"
          [error]="dialogError.error"
          [dismissible]="true"
          (dismissed)="dialogError.clear()"
        ></arp-error-block>
      }

      <div class="modal-footer">
        @if (dialogError.error?.blockedReason) {
          <span class="modal-footer-hint">{{ dialogError.error.blockedReason }}</span>
        }

        <button type="button" class="arpio-secondary-button" (click)="close()">Cancel</button>
        <button
          type="submit"
          class="arpio-button"
          [disabled]="!isFormValid || saving || !!dialogError.error?.blockedReason"
        >
          @if (saving) {
            <svg lucideLoaderCircle class="lucide-spin"></svg>
          }
          {{ getSaveButtonText() }}
        </button>
      </div>
    </form>
  </div>
</ng-template>
